import { SaveButton } from '@core/components/Buttons';
import { CompetenciaInput, DateInput, NumberInput, TextInput, TextLabel } from '@core/components/Inputs';
import { FormControlLabel, Grid, Switch } from '@material-ui/core';
import InputMonetario from 'app/components/Input/InputMonetario';
import SelectConta from 'app/components/Select/SelectConta';
import SelectFornecedor from 'app/components/Select/SelectFornecedor';
import { useFormikContext } from 'formik';
import React from 'react';
import { ContasPagarUtil } from '../store/contasPagarAction';
import ContasPagarAproveitamentoCredito from './ContasPagarAproveitamentoCredito';
import ContasPagarCategorias from './ContasPagarCategorias';
import ContasPagarParcelas from './ContasPagarParcelas';
import FormaDePagamento from './FormaDePagamento';
import SwitchFavorecidoFornecedor from './SwitchFavorecidoFornecedor';

const ORIGENS_LANCAMENTO = {
  CONTA_PAGAR: 'Conta a pagar',
  COMPRA: 'Compras',
  COMPRA_PRODUTO: 'Compras',
  COMPRA_SERVICO: 'Compras',
  CONCILIACAO: 'Conciliação bancária',
};

const APROVEITAMENTO_CREDITO_DEFAULT = {
  valorCreditoIbs: null,
  valorCreditoCbs: null,
  tipoAproveitamentoCredito: 'NAO_APROVEITA_CREDITO',
  aliquotaIbs: null,
  aliquotaCbs: null,
  percentualCredito: null,
  observacaoJustificativa: '',
  ativo: true,
};

/*
RNE-106 - Alteração do valor total das parcelas : Public <<Functional>> Feature
Created:	23/03/2020 09:41:02
Modified:	23/03/2020 13:42:45
Se um dos campos "Valor total", "Número de parcelas" e/ou "Data de vencimento da 1ª parcela" 
for alterado no lançamento de conta parcelada, o sistema deve apagar as parcelas que foram geradas e, 
somente quando o usuário acionar para "Gerar", o sistema deve distribuir o valor igualmente nas parcelas
conforme novas informações alteradas. Se os valores forem alterados diretamente nas parcelas,
o sistema não deve alterar os outros valores das parcelas automaticamente.
Caso o usuário insira mais parcelas, o sistema deve desconsiderar o número de parcelas inseridas
antes da geração e, ao salvar, o sistema deve atualizar essa informação na despesa.
*/
const onChangeParcela = (handleChange, values) => {
  return (event) => {
    if (values?.origemLancamento?.includes('COMPRA')) {
      handleChange({ target: { id: 'retencoes', value: values?.retencoesCompra } });
    } else {
      handleChange({ target: { id: 'retencoes', value: undefined } });
    }
    handleChange(event);
    handleChange({ target: { id: 'parcelamento', value: undefined } });

    if (event.target.id === 'conta') {
      handleChange({ target: { id: 'formaPagamento', value: undefined } });
    }
  };
};

const ContasPagarParcelada = ({
  handleChange,
  handleBlur,
  values,
  errors,
  touched,
  submitCount,
  origemCompra,
  empresaIbsCbs = false,
}) => {
  const parcelamento = values?.parcelamento;
  const formik = useFormikContext();

  const sizes = {
    competencia: {
      xs: 12,
      sm: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 3 : 4,
      md: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 3 : 4,
      lg: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 3 : 4,
      xl: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 3 : 4,
    },
    parcelas: {
      xs: 12,
      sm: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 3 : 4,
      md: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 3 : 4,
      lg: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 3 : 4,
      xl: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 3 : 4,
    },
    origem: {
      xs: 12,
      sm: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 2 : 0,
      md: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 2 : 0,
      lg: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 2 : 0,
      xl: !!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' ? 2 : 0,
    },
  };

  /*
  RNE-478 - Edição de conta a pagar
  Para as contas a pagar que estiverem com situação "Baixada", somente a aba “Anexar documento” deve ser exibida com possibilidade de edição. Todos os outros campos das outras abas devem ser exibidos desabilitados para edição.
  */
  const contaBaixada =
    values?.situacao === 'BAIXADA' || values?.situacao === 'PARCIALMENTE_BAIXADA' || values?.situacao === 'PARCIAL';

  const handleGerar = () => {
    handleChange({
      target: { id: 'gerarParcelasCount', value: values?.gerarParcelasCount ? values.gerarParcelasCount + 1 : 1 },
    });
    formik.validateForm();
    ContasPagarUtil.handleGerarParcelas(values, handleChange, values.valorTotal)();
  };

  const _errors = {
    valorTotal: (submitCount > 0 || values?.gerarParcelasCount > 0 || touched?.valorTotal) && errors?.valorTotal,
    competencia: (submitCount > 0 || values?.gerarParcelasCount > 0 || touched?.competencia) && errors?.competencia,
    numeroParcelas:
      (submitCount > 0 || values?.gerarParcelasCount > 0 || touched?.numeroParcelas) && errors?.numeroParcelas,
    dataVencimento:
      (submitCount > 0 || values?.gerarParcelasCount > 0 || touched?.dataVencimento) && errors?.dataVencimento,
    fornecedor: (submitCount > 0 || touched?.fornecedor?.id) && errors?.fornecedor,
    conta: (submitCount > 0 || touched?.conta) && errors?.conta,
    formaPagamento: (submitCount > 0 || touched?.formaPagamento) && errors?.formaPagamento,
  };

  const temParcelaBaixada = values?.parcelamento?.parcelas?.some(
    (parcela) => parcela?.situacao === 'BAIXADA' || parcela?.situacao === 'PARCIAL'
  );

  const isEdicao = !!values?.id && values?.situacao === 'PARCIAL';

  return (
    <>
      <Grid
        item
        xs={sizes.competencia.xs}
        sm={sizes.competencia.sm}
        md={sizes.competencia.md}
        lg={sizes.competencia.lg}
        xl={sizes.competencia.xl}
      >
        <CompetenciaInput
          required
          id="competencia"
          label="Mês competência"
          value={values?.competencia}
          // "Se um dos campos "Valor total", "Número de parcelas" e/ou "Data de vencimento da 1ª parcela" "
          // Não tem nada falando de competência.
          onChange={handleChange}
          fullWidth
          onBlur={handleBlur}
          touched={touched?.competencia}
          error={touched?.competencia && errors?.competencia}
          helperText={errors?.competencia}
          disabled={temParcelaBaixada || contaBaixada}
          format="MM/YYYY"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMonetario
          fullWidth
          required
          autoFocus
          id="valorTotal"
          label="Valor total (R$)"
          handleBlur={handleBlur}
          value={values?.valorTotal}
          error={_errors.valorTotal}
          touched={!!_errors.valorTotal}
          helperText={errors?.valorTotal}
          handleChange={onChangeParcela(handleChange, values)}
          disabled={temParcelaBaixada || contaBaixada || origemCompra}
        />
      </Grid>
      <Grid
        item
        xs={sizes.parcelas.xs}
        sm={sizes.parcelas.sm}
        md={sizes.parcelas.md}
        lg={sizes.parcelas.lg}
        xl={sizes.parcelas.xl}
      >
        <NumberInput
          required
          label="Número de parcelas"
          id="numeroParcelas"
          value={values?.numeroParcelas}
          onChange={onChangeParcela(handleChange, values)}
          onBlur={handleBlur}
          touched={!!_errors?.numeroParcelas}
          error={_errors?.numeroParcelas}
          disabled={temParcelaBaixada || contaBaixada || origemCompra}
          decimalScale={0}
          isAllowed={(val) => {
            const { formattedValue, floatValue } = val;
            return formattedValue === '' || floatValue <= 72;
          }}
        />
      </Grid>
      {!!values?.id && values?.origemLancamento !== 'CONTA_PAGAR' && (
        <Grid
          item
          xs={sizes.origem.xs}
          sm={sizes.origem.sm}
          md={sizes.origem.md}
          lg={sizes.origem.lg}
          xl={sizes.origem.xl}
        >
          <TextLabel
            id="origemLancamento"
            label="Origem do lançamento"
            value={ORIGENS_LANCAMENTO[values?.origemLancamento]}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={4}>
        <DateInput
          fullWidth
          required
          id="dataVencimento"
          label="Data do vencimento da 1ª parcela"
          value={values?.dataVencimento}
          onBlur={handleBlur}
          onChange={onChangeParcela(handleChange, values)}
          touched={!!_errors?.dataVencimento}
          error={_errors?.dataVencimento}
          helperText={errors?.dataVencimento}
          disabled={temParcelaBaixada || contaBaixada || isEdicao}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <SelectConta
          id="conta"
          fullWidth
          label="Conta"
          value={values?.conta}
          onChange={onChangeParcela(handleChange, values)}
          onBlur={handleBlur}
          maxLength={100}
          disabled={temParcelaBaixada || contaBaixada || isEdicao}
          error={_errors?.conta}
          touched={!!_errors?.conta}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <FormaDePagamento
          value={values?.formaPagamento}
          handleChange={onChangeParcela(handleChange, values)}
          handleBlur={handleBlur}
          tipoConta={values?.conta?.tipo}
          disabled={temParcelaBaixada || contaBaixada || isEdicao || !values?.conta}
          error={_errors?.formaPagamento}
          touched={!!_errors?.formaPagamento}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <SaveButton
          type="button"
          color="buttonSave"
          onClick={handleGerar}
          className="mt-10"
          disabled={temParcelaBaixada || contaBaixada}
        >
          Gerar
        </SaveButton>
      </Grid>

      {parcelamento?.parcelas && (
        <>
          <ContasPagarParcelas
            handleChange={handleChange}
            handleBlur={handleBlur}
            values={values}
            errors={errors}
            touched={touched}
            origemCompra={origemCompra}
          />

          <Grid item xs={12} sm={6} md={6}>
            <SelectFornecedor
              handleChange={handleChange}
              handleBlur={handleBlur}
              fornecedor={values?.fornecedor}
              touched={!!_errors?.fornecedor}
              error={_errors?.fornecedor}
              disabled={origemCompra || contaBaixada}
              required
            />
          </Grid>
          <SwitchFavorecidoFornecedor
            checked={values?.favorecidoDiferenteFornecedor}
            handleChange={handleChange}
            handleBlur={handleBlur}
            disabled={contaBaixada}
          />

          {values?.favorecidoDiferenteFornecedor && (
            <Grid item xs={12} sm={6} md={6}>
              <SelectFornecedor
                label="Favorecido"
                idObject="favorecido"
                id="favorecido.id"
                handleChange={handleChange}
                handleBlur={handleBlur}
                fornecedor={values?.favorecido}
                touched={touched?.favorecido?.id}
                error={errors?.favorecido?.id}
                disabled={contaBaixada}
              />
            </Grid>
          )}

          <ContasPagarCategorias
            handleChange={handleChange}
            handleBlur={handleBlur}
            values={values}
            errors={errors?.categorias}
            touched={touched?.categorias}
            disabled={temParcelaBaixada || contaBaixada}
            origemCompra={origemCompra}
            disabledDescricao={contaBaixada}
          />

          <Grid item xs={12} sm={6}>
            <TextInput
              id="documentoFiscal"
              fullWidth
              label="Documento fiscal"
              value={values?.documentoFiscal}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={30}
              disabled={origemCompra || contaBaixada}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateInput
              fullWidth
              disableFuture
              id="dataEmissao"
              label="Data da emissão"
              value={values?.dataEmissao}
              onChange={handleChange}
              disabled={origemCompra || contaBaixada}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <TextInput
              id="observacao"
              name="observacao"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.observacao}
              error={errors.observacao}
              touched={touched.observacao}
              disabled={contaBaixada || origemCompra}
              fullWidth
              multiline
              maxLength={500}
              rows={3}
              variant="outlined"
              label="Observação"
            />
          </Grid>

          {empresaIbsCbs && (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  label="Aproveitamento de crédito IBS/CBS"
                  control={
                    <Switch
                      id="aproveitamentoCreditoAtivo"
                      name="aproveitamentoCreditoAtivo"
                      value={values?.aproveitamentoCreditoAtivo}
                      checked={values?.aproveitamentoCreditoAtivo}
                      color="primary"
                      onChange={(event) => {
                        if (event.target.checked) {
                          handleChange({
                            target: {
                              id: 'aproveitamentoCredito',
                              value: APROVEITAMENTO_CREDITO_DEFAULT,
                            },
                          });
                        } else {
                          handleChange({ target: { id: 'aproveitamentoCredito', value: null } });
                        }
                        handleChange(event);
                      }}
                      onBlur={handleBlur}
                      disabled={origemCompra || contaBaixada}
                    />
                  }
                />
              </Grid>
              {values?.aproveitamentoCreditoAtivo && (
                <>
                  <ContasPagarAproveitamentoCredito
                    values={values}
                    errors={errors}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    touched={touched}
                    aproveitamentoCreditoFlag={values?.aproveitamentoCreditoAtivo}
                    disabled={origemCompra || contaBaixada}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
export default ContasPagarParcelada;
